import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EnumAPIStatus } from '@api/AbstractAPI';
import { ModalController, NavParams } from '@ionic/angular';
import { IFilterList } from '@models/Content';
import { EnumGenericRoleActionAlerts } from '@pages/BasePage';
import { FormValidators } from '@validators/form.validators';
import { EnumUserGenre } from '@models/User';
import { EnumDocumentType } from '@models/Common';
import { CommonService } from '@services/common.service';

export enum EnumSelectManagerProfileMode {
    Available = 'available',
    SearchUsers = 'searchUsers',
    Create = 'create'
}

@Component({
    selector: 'app-select-manager-profile',
    templateUrl: './select-manager-profile.component.html',
    styleUrls: ['./select-manager-profile.component.scss'],
})
export class SelectManagerProfileModalComponent implements OnInit {
    public EnumSelectManagerProfileMode = EnumSelectManagerProfileMode;

    public Data: any[] = [];
    public ExistingUsersData: any[] = [];
    public Profiles: any[] = [];
    public Mode: EnumSelectManagerProfileMode = EnumSelectManagerProfileMode.Available;
    public IsSubmitting: boolean = false;

    public CreateForm: FormGroup;

    public Countries: any[] = [];

    public EnumUserGenre = EnumUserGenre;
    public UserDocumentType = EnumDocumentType;

    public MaxValidityDate: string = "";
    public MinValidityDate: string = "";

    public ShowExtraData: boolean = false;

    private expandedManagerLists: Set<string> = new Set();

    private onSave: Function;
    private onGetUsers: Function;
    private onGetExistingUsers: Function;
    private onCreate: Function;
    private getProfiles: Function;
    private isAdmin: boolean;
    private idMerchant: string;
    private managers: string[];

    public Filters: IFilterList;

    constructor(
        private NavParams: NavParams,
        private ModalController: ModalController,
        private CommonService: CommonService
    ) {
        this.Filters = {
            PageNumber: 1,
            PageRows: 25,
            Text: ''
        };

        this.onSave = this.NavParams?.get('onSave') || null;
        this.onGetUsers = this.NavParams?.get('getUsers') || null;
        this.onGetExistingUsers = this.NavParams?.get('getExistingUsers') || null;
        this.onCreate = this.NavParams?.get('onCreate') || null;
        this.getProfiles = this.NavParams?.get('getProfiles') || null;
        this.isAdmin = this.NavParams?.get('isAdmin') || false;
        this.idMerchant = this.NavParams?.get('idMerchant') || '';
        this.managers = this.NavParams?.get('managers') || [];

        const currentDate = new Date();

        currentDate.setFullYear(currentDate.getFullYear() + 100);

        this.MaxValidityDate = currentDate.toISOString();

        this.MinValidityDate = new Date().toISOString();

        this.CreateForm = new FormGroup({
            Name: new FormControl('', [Validators.required]),
            Email: new FormControl('', [Validators.required, FormValidators.EmailValidator]),
            IdTemplate: new FormControl(null, [Validators.required]),
            Phone: new FormControl(null, [FormValidators.TelephoneValidator]),
            MobilePhone: new FormControl(null, [FormValidators.TelephoneValidator]),

            IsExternal: new FormControl(false),
            EmailVerified: new FormControl(false),

            VatNumber: new FormControl(null, [FormValidators.NIFValidator]),
            IdDocumentType: new FormControl(EnumDocumentType.BI),
            IdentificationDocumentNumber: new FormControl(null),
            IdentificationDocumentValidityDate: new FormControl(null),
            IsEntityResident: new FormControl(null),
            BirthDate: new FormControl(null),
            IdGender: new FormControl(EnumUserGenre.Undefined),
            IdNationality: new FormControl(13),
        });
    }

    async ngOnInit() {

        if (this.getProfiles) {
            this.Profiles = await this.getProfiles() || [];
        }

        if (this.onGetUsers) {
            this.Data = await this.onGetUsers() || [];

            this.Data.forEach(item => item.MerchantList = item?.MerchantList?.filter(it => it?.IsMerchantAssociated) || []);
        }

        const responseCountries = await this.CommonService.GetCountryList();

        this.Countries = responseCountries?.Result || [];

    }


    async OnSearch(ev?: any, text: string = '') {

        if ((ev.type == 'ionClear' && this.Filters.Text == '') || (text == this.Filters.Text)) return;

        this.Filters.PageNumber = 1;
        this.Filters.Text = text;

        if (this.Filters.Text == '') return this.Data = [];

        if (this.onGetExistingUsers)
            this.ExistingUsersData = await this.onGetExistingUsers(this.Filters);
    }

    public OnChangeMode(ev: any) {
        this.Mode = ev?.detail?.value;
    }

    public get HasCreateOption(): boolean {
        return !!this.onCreate;
    }

    public get HasSearchUsersOption(): boolean {
        return this.isAdmin && !!this.onGetExistingUsers;
    }

    /**
     * 
     * @param item 
     */
    public async OnSaveAssociation(item: any) {

        if (this.onSave) {
            const result = await this.onSave([item?.User?.Id || item?.Id]);
            if (result?.Status == EnumAPIStatus.Success)
                this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Confirm);
        }
    }

    /**
     * 
     * @param item 
     * @returns 
     */
    public IsAssociatedToCurrentMerchant(item: any): boolean {

        return !!item?.MerchantList?.find(it => it?.Merchant?.Id == this.idMerchant)?.IsMerchantAssociated
            || this.managers?.includes(item?.User?.Id || item?.Id) || false;
    }

    /**
     * 
     * @param item 
     */
    public ToggleMerchantList(item: any) {
        const id = item?.User?.Id;

        if (this.expandedManagerLists.has(id))
            this.expandedManagerLists.delete(id);
        else
            this.expandedManagerLists.add(id);
    }

    /**
     * 
     * @param item 
     * @returns 
     */
    public IsMerchantListExpanded(item: any): boolean {
        return this.expandedManagerLists.has(item?.User?.Id);
    }

    /**
     * 
     * @returns 
     */
    public async OnSubmitCreate() {
        this.CreateForm.markAllAsTouched();

        if (this.CreateForm.invalid || !this.onCreate) return;

        this.IsSubmitting = true;

        const data = {
            Name: this.CreateForm.get('Name')?.value || '',
            Contact: {
                Email: this.CreateForm.get('Email')?.value || '',
                Phone: this.CreateForm.get('Phone')?.value || '',
                MobilePhone: this.CreateForm.get('MobilePhone')?.value || '',
            },
            IdTemplate: this.CreateForm.get('IdTemplate')?.value || null,
            IsExternal: this.CreateForm.get('IsExternal')?.value || null,
            EmailVerified: this.CreateForm.get('EmailVerified')?.value || null,
            VatNumber: this.CreateForm.get('VatNumber')?.value || null,
            IdDocumentType: this.CreateForm.get('IdDocumentType')?.value || null,
            IdentificationDocumentNumber: this.CreateForm.get('IdentificationDocumentNumber')?.value || null,
            IdentificationDocumentValidityDate: this.CreateForm.get('IdentificationDocumentValidityDate')?.value || null,
            IsEntityResident: this.CreateForm.get('IsEntityResident')?.value ?? null,
            BirthDate: this.CreateForm.get('BirthDate')?.value || null,
            IdGender: this.CreateForm.get('IdGender')?.value || null,
            IdNationality: this.CreateForm.get('IdNationality')?.value || null
        };

        const responseUser = await this.onCreate(data);

        this.IsSubmitting = false;

        if (responseUser?.Status == EnumAPIStatus.Success) {


            if (this.onSave && responseUser?.Result?.Id) {
                const result = await this.onSave([responseUser?.Result?.Id], false);


            }

            this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Confirm);
        }
    }

    Close() {
        this.ModalController.dismiss(null, EnumGenericRoleActionAlerts.Close);
    }
}
